import { isNative } from "@/lib/appConfig";
import { initExternalLinks } from "@/lib/externalLinks";

// One-time native shell setup. No-op on web. Capacitor plugins are imported
// dynamically so the web bundle never pulls native code.
export async function initNative() {
    if (!isNative()) return;

    // Lets CSS scope safe-area insets to native only (see index.css).
    document.documentElement.classList.add("capacitor-native");

    // Hide the splash FIRST and in isolation. launchAutoHide also hides it on a
    // native timer as a safety net, but doing it here removes it the instant the
    // WebView is up. Nothing below (status bar, keyboard, chunk loads) can block
    // it, and a failed chunk import falls through the catch instead of leaving the
    // splash stuck on screen forever (the cold-start hang we saw on iOS).
    try {
        const { SplashScreen } = await import("@capacitor/splash-screen");
        await SplashScreen.hide();
    } catch {
        // splash-screen plugin unavailable
    }

    // Route external links to the system browser.
    initExternalLinks();

    try {
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        // Light background (#F5F1EA) → dark status-bar text.
        await StatusBar.setStyle({ style: Style.Light });
        if (typeof StatusBar.setBackgroundColor === "function") {
            // Android-only; harmless no-op elsewhere.
            await StatusBar.setBackgroundColor({ color: "#F5F1EA" }).catch(() => {});
        }
    } catch {
        // status-bar plugin unavailable
    }

    try {
        const { Keyboard } = await import("@capacitor/keyboard");
        // Scroll the focused field into view so the keyboard never covers it.
        // A 120 ms delay lets the keyboard animation start before we measure layout.
        // scrollIntoView with block:"center" works for top-level scroll; for inputs
        // inside a Dialog/Sheet (which have their own overflow-y:auto container) we
        // also walk up the DOM and scroll the nearest scrollable ancestor directly —
        // otherwise the field stays hidden behind the keyboard even though the page
        // itself hasn't scrolled.
        Keyboard.addListener("keyboardWillShow", () => {
            const el = document.activeElement;
            if (!el || (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA" && !el.isContentEditable)) return;
            setTimeout(() => {
                el.scrollIntoView({ block: "center", behavior: "smooth" });
                // Also scroll the nearest scrollable ancestor (e.g. modal body).
                let node = el.parentElement;
                while (node && node !== document.body) {
                    const style = window.getComputedStyle(node);
                    const overflowY = style.overflowY;
                    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
                        const rect = el.getBoundingClientRect();
                        const containerRect = node.getBoundingClientRect();
                        const relativeTop = rect.top - containerRect.top;
                        const targetScroll = node.scrollTop + relativeTop - containerRect.height / 2 + rect.height / 2;
                        node.scrollTo({ top: targetScroll, behavior: "smooth" });
                        break;
                    }
                    node = node.parentElement;
                }
            }, 120);
        });
    } catch {
        // keyboard plugin unavailable
    }
}
